import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { checkClientRateLimit } from '../lib/validation'
import PasswordInput from '../components/PasswordInput'

const FP = "'Playfair Display', Georgia, serif"
const FB = "'DM Sans', system-ui, sans-serif"

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const showVerifyNotice = searchParams.get('verify') === '1'
  const [email, setEmail]           = useState('')
  const [password, setPassword]     = useState('')
  const [loading, setLoading]       = useState(false)
  const [error, setError]           = useState(null)
  const [forgotMode, setForgotMode] = useState(false)
  const [sent, setSent]             = useState(false)
  const [resending, setResending]   = useState(false)
  const [resent, setResent]         = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    if (!checkClientRateLimit('bellason_login_attempts', 5, 5 * 60 * 1000)) {
      setError('Demasiados intentos. Espera unos minutos.')
      setLoading(false)
      return
    }

    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password })

    if (authError) {
      setError('Correo o contraseña incorrectos')
      setLoading(false)
      return
    }

    if (!data.user?.email_confirmed_at && !data.user?.confirmed_at) {
      await supabase.auth.signOut()
      setError('Verifica tu correo antes de entrar. Te enviamos un enlace al registrarte.')
      setLoading(false)
      return
    }

    await supabase.auth.getSession()

    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('role, is_blocked')
      .eq('id', data.user.id)
      .single()

    if (profileError) {
      setError('No se encontró el perfil. Contacta a soporte.')
      setLoading(false)
      return
    }

    if (profile?.is_blocked) {
      await supabase.auth.signOut()
      setError('Tu cuenta está suspendida. Contacta soporte.')
      setLoading(false)
      return
    }

    setLoading(false)
    if (profile?.role === 'client') {
      navigate('/mis-solicitudes', { replace: true })
    } else if (profile?.role === 'provider') {
      navigate('/solicitudes', { replace: true })
    } else if (profile?.role === 'admin') {
      navigate('/admin', { replace: true })
    } else {
      setError('Rol no reconocido.')
    }
  }

  async function resendVerification() {
    if (!email) { setError('Escribe tu correo primero'); return }
    setResending(true)
    setError(null)
    const { error: rErr } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/login` },
    })
    setResending(false)
    if (rErr) { setError(rErr.message); return }
    setResent(true)
  }

  async function handleForgot(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) { setError(error.message); setLoading(false); return }
    setSent(true)
    setLoading(false)
  }

  // ─── Forgot mode ────────────────────────────────────────────────────
  if (forgotMode) {
    return (
      <div style={S.container}>
        <div style={S.card}>
          <button type="button" onClick={() => { setForgotMode(false); setSent(false) }} style={S.backBtn}>
            ← Volver
          </button>

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img src="/logo-v1.png" alt="Bellason" width={32} height={32} style={{ display: 'inline-block', marginBottom: '12px', objectFit: 'contain' }} />
            <h1 style={S.title}>Recuperar acceso</h1>
            <p style={S.subtitle}>Te enviamos un enlace a tu correo</p>
          </div>

          {sent ? (
            <p style={{ color: 'var(--bln-accent)', textAlign: 'center', fontFamily: FP, fontStyle: 'italic', fontSize: '15px' }}>
              Correo enviado. Revisa tu bandeja.
            </p>
          ) : (
            <form onSubmit={handleForgot} style={S.form}>
              <label htmlFor="forgot-email" style={S.srOnly}>Correo electrónico</label>
              <input id="forgot-email" name="email" autoComplete="email"
                style={S.input} placeholder="Correo electrónico" type="email"
                value={email} onChange={e => setEmail(e.target.value)} required />
              {error && <p style={S.error}>⚠ {error}</p>}
              <button type="submit" disabled={loading} style={S.btnPrimary}>
                {loading ? 'Enviando…' : 'Enviar correo'}
              </button>
            </form>
          )}
        </div>
      </div>
    )
  }

  // ─── Login form ──────────────────────────────────────────────────────
  return (
    <div style={S.container}>
      <div style={S.card}>
        <button type="button" onClick={() => navigate('/')} style={S.backBtn}>← Volver</button>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/logo-v1.png" alt="Bellason" width={32} height={32} style={{ display: 'inline-block', marginBottom: '12px', objectFit: 'contain' }} />
          <h1 style={S.title}>Bellason</h1>
          <p style={S.subtitle}>Inicia sesión</p>
        </div>

        {(showVerifyNotice || resent) && (
          <div style={S.banner}>
            <p style={{ margin: 0, fontSize: '13px', fontWeight: 300, color: 'var(--bln-accent)' }}>
              {resent ? 'Correo de verificación reenviado. Revisa tu bandeja.' : 'Confirma tu correo. Te enviamos un enlace al registrarte.'}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={S.form}>
          <label htmlFor="login-email" style={S.srOnly}>Correo electrónico</label>
          <input id="login-email" name="email" autoComplete="email"
            style={S.input} placeholder="Correo electrónico" type="email"
            value={email} onChange={e => setEmail(e.target.value)} required />

          <label htmlFor="login-password" style={S.srOnly}>Contraseña</label>
          <PasswordInput id="login-password" name="password" autoComplete="current-password"
            style={S.input} placeholder="Contraseña"
            value={password} onChange={e => setPassword(e.target.value)} required />

          {error && <p style={S.error}>⚠ {error}</p>}

          <button type="submit" disabled={loading} style={S.btnPrimary}>
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        {error && error.toLowerCase().includes('verifica') && (
          <p style={S.textLink} onClick={resendVerification}>
            {resending ? 'Reenviando...' : 'Reenviar correo de verificación'}
          </p>
        )}

        <p style={S.textLink} onClick={() => setForgotMode(true)}>
          ¿Olvidaste tu contraseña?
        </p>

        <p style={S.link}>
          ¿No tienes cuenta?{' '}
          <Link to="/registro" style={{ color: 'var(--bln-accent)' }}>Regístrate</Link>
        </p>
      </div>
    </div>
  )
}

const S = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bln-bg)',
    padding: '1.5rem 1rem',
    fontFamily: FB,
  },
  card: {
    backgroundColor: 'var(--bln-bg)',
    padding: '2.5rem 2rem',
    borderRadius: '14px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid var(--bln-accent-tint)',
  },
  title: {
    fontFamily: FP,
    color: 'var(--bln-text)',
    fontSize: '1.8rem',
    fontWeight: 400,
    textAlign: 'center',
    margin: '0 0 4px',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    margin: 0,
    fontSize: '13px',
    fontWeight: 300,
    lineHeight: 1.6,
  },
  banner: {
    padding: '10px 14px',
    borderRadius: '8px',
    border: '1px solid var(--bln-accent-tint)',
    background: 'rgba(168,85,247,0.04)',
    marginBottom: '16px',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid var(--bln-accent-tint)',
    backgroundColor: 'transparent',
    color: 'var(--bln-text)',
    fontSize: '14px',
    fontFamily: FB,
    fontWeight: 300,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    padding: '13px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--bln-accent)',
    color: '#fff',
    fontWeight: 500,
    fontSize: '14px',
    fontFamily: FB,
    cursor: 'pointer',
    marginTop: '4px',
    letterSpacing: '0.2px',
  },
  error: {
    color: 'var(--bln-danger)',
    fontSize: '13px',
    margin: 0,
    padding: '8px 12px',
    background: 'rgba(248,113,113,0.06)',
    borderRadius: '8px',
    border: '1px solid rgba(248,113,113,0.15)',
    fontWeight: 300,
  },
  textLink: {
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    marginTop: '14px',
    fontSize: '13px',
    fontWeight: 300,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  link: {
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 300,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--bln-text-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '0 0 1.25rem 0',
    display: 'block',
    fontFamily: FB,
    fontWeight: 300,
  },
  srOnly: {
    position: 'absolute',
    width: '1px', height: '1px',
    padding: 0, margin: '-1px',
    overflow: 'hidden', clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap', border: 0,
  },
}